import { useEffect } from 'react';

/**
 * `options` is merged into the IntersectionObserver config. Omit it and the observer
 * behaves exactly as before: it fires only once the sentinel is fully on screen, i.e. once
 * the list has already bottomed out. Pass a rootMargin to start paging ahead of that.
 *
 * Note `options` lands in the dependency list, so callers must hand in a stable object
 * (module-level const or useMemo) rather than an inline literal.
 */
const useInfiniteScrollObserver = (observerTarget, callBack, bottomTrigger, options) => {
  useEffect(() => {
    // Captured so the cleanup unobserves the node this effect actually observed.
    const target = observerTarget.current;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          callBack();
          bottomTrigger(true);
        } else {
          bottomTrigger(false);
        }
      },
      { threshold: 1, ...options }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [observerTarget, callBack, bottomTrigger, options]);
};

export default useInfiniteScrollObserver;
